import { apiService } from './api';
import type {
  AuthResponseDto,
  TwoFactorEnableResponseDto,
  TwoFactorLoginCompleteSetupResponse,
  TwoFactorSetupDto
} from './authService';

function normalizeAuthResponse(raw: Record<string, unknown>): AuthResponseDto {
  return {
    success: Boolean(raw.success ?? raw.Success),
    message: String(raw.message ?? raw.Message ?? '')
  };
}

function normalizeEnableResponse(raw: Record<string, unknown>): TwoFactorEnableResponseDto {
  return {
    success: Boolean(raw.success ?? raw.Success),
    message: String(raw.message ?? raw.Message ?? ''),
    recoveryCodes: (raw.recoveryCodes ?? raw.RecoveryCodes ?? []) as string[]
  };
}

/** API may return camelCase or PascalCase depending on serializer config. */
export type TwoFactorMethodKind = 'none' | 'authenticator' | 'email' | 'telegram' | 'bale';

export interface TwoFactorStatusDto {
  twoFactorEnabled: boolean;
  hasAuthenticator: boolean;
  recoveryCodesLeft: number;
  hasPassword: boolean;
  selectedTwoFactorMethod: TwoFactorMethodKind;
  emailChannelAvailable: boolean;
  telegramChannelAvailable: boolean;
  baleChannelAvailable: boolean;
  telegramLinked: boolean;
  baleLinked: boolean;
  maskedChannelPhone?: string | null;
  phoneNumber?: string | null;
  twoFactorChannelPhone?: string | null;
  accountEmail?: string | null;
  telegramBotUsername?: string | null;
  baleBotUsername?: string | null;
}

function normalizeStatus(raw: Record<string, unknown>): TwoFactorStatusDto {
  const methodRaw = String(raw.selectedTwoFactorMethod ?? raw.SelectedTwoFactorMethod ?? 'none').toLowerCase();
  const method: TwoFactorMethodKind =
    methodRaw === 'email' || methodRaw === 'telegram' || methodRaw === 'bale' || methodRaw === 'authenticator'
      ? methodRaw
      : 'none';

  return {
    twoFactorEnabled: Boolean(raw.twoFactorEnabled ?? raw.TwoFactorEnabled),
    hasAuthenticator: Boolean(raw.hasAuthenticator ?? raw.HasAuthenticator),
    recoveryCodesLeft: Number(raw.recoveryCodesLeft ?? raw.RecoveryCodesLeft ?? 0),
    hasPassword: Boolean(raw.hasPassword ?? raw.HasPassword ?? true),
    selectedTwoFactorMethod: method,
    emailChannelAvailable: Boolean(raw.emailChannelAvailable ?? raw.EmailChannelAvailable ?? true),
    telegramChannelAvailable: Boolean(raw.telegramChannelAvailable ?? raw.TelegramChannelAvailable ?? true),
    baleChannelAvailable: Boolean(raw.baleChannelAvailable ?? raw.BaleChannelAvailable ?? true),
    telegramLinked: Boolean(raw.telegramLinked ?? raw.TelegramLinked),
    baleLinked: Boolean(raw.baleLinked ?? raw.BaleLinked),
    maskedChannelPhone: (raw.maskedChannelPhone ?? raw.MaskedChannelPhone) as string | null | undefined,
    phoneNumber: (raw.phoneNumber ?? raw.PhoneNumber) as string | null | undefined,
    twoFactorChannelPhone: (raw.twoFactorChannelPhone ?? raw.TwoFactorChannelPhone) as string | null | undefined,
    accountEmail: (raw.accountEmail ?? raw.AccountEmail) as string | null | undefined,
    telegramBotUsername: (raw.telegramBotUsername ?? raw.TelegramBotUsername) as string | null | undefined,
    baleBotUsername: (raw.baleBotUsername ?? raw.BaleBotUsername) as string | null | undefined
  };
}

class TwoFactorService {
  async getStatus(): Promise<TwoFactorStatusDto> {
    const raw = await apiService.get<Record<string, unknown>>('/api/Auth/two-factor/status', {
      cache: false
    });
    return normalizeStatus(raw ?? {});
  }

  setup(reset = false, password?: string | null): Promise<TwoFactorSetupDto> {
    const q = reset ? '?reset=true' : '';
    return apiService.post<TwoFactorSetupDto>(`/api/Auth/two-factor/setup${q}`, {
      password: password?.trim() ? password : null
    });
  }

  setupAtLogin(twoFactorToken: string): Promise<TwoFactorSetupDto> {
    return apiService.post<TwoFactorSetupDto>('/api/Auth/two-factor/setup-login', { twoFactorToken });
  }

  enable(code: string): Promise<TwoFactorEnableResponseDto> {
    return apiService.post<TwoFactorEnableResponseDto>('/api/Auth/two-factor/enable', { code });
  }

  disable(code: string, password?: string | null): Promise<AuthResponseDto> {
    const body: { code: string; password?: string } = { code };
    const trimmedPassword = password?.trim();
    if (trimmedPassword) {
      body.password = trimmedPassword;
    }
    return apiService.post<AuthResponseDto>('/api/Auth/two-factor/disable', body);
  }

  completeLogin(twoFactorToken: string, code: string): Promise<AuthResponseDto> {
    return apiService.post<AuthResponseDto>('/api/Auth/two-factor/login', { twoFactorToken, code });
  }

  completeSetupLogin(twoFactorToken: string, code: string): Promise<TwoFactorLoginCompleteSetupResponse> {
    return apiService.post<TwoFactorLoginCompleteSetupResponse>(
      '/api/Auth/two-factor/complete-setup-login',
      { twoFactorToken, code }
    );
  }

  async sendChannelSetupCode(method: TwoFactorMethodKind, phoneNumber?: string): Promise<AuthResponseDto> {
    const raw = await apiService.post<Record<string, unknown>>('/api/Auth/two-factor/channel/send-setup-code', {
      method,
      phoneNumber: phoneNumber?.trim() || null
    });
    return normalizeAuthResponse(raw ?? {});
  }

  async enableChannel(method: TwoFactorMethodKind, code: string): Promise<TwoFactorEnableResponseDto> {
    const raw = await apiService.post<Record<string, unknown>>('/api/Auth/two-factor/channel/enable', { method, code });
    return normalizeEnableResponse(raw ?? {});
  }

  resendLoginCode(twoFactorToken: string): Promise<AuthResponseDto> {
    return apiService.post<AuthResponseDto>('/api/Auth/two-factor/login/resend', { twoFactorToken });
  }

  getTelegramLink(): Promise<{ linkToken: string; deepLink: string; botUsername: string }> {
    return apiService.get('/api/Auth/two-factor/telegram/link', { cache: false });
  }

  getTelegramLinkStatus(linkToken: string): Promise<{ linked: boolean; phoneNumber?: string }> {
    return apiService.get(`/api/Auth/two-factor/telegram/link-status?linkToken=${encodeURIComponent(linkToken)}`, {
      cache: false
    });
  }

  /** Development API only — link by chat id (from @userinfobot), no link token required. */
  linkTelegramChatId(telegramChatId: number, phoneNumber?: string): Promise<{ linked: boolean; phoneNumber?: string }> {
    return apiService.post('/api/Auth/two-factor/telegram/link-chat-id', {
      telegramChatId,
      phoneNumber: phoneNumber?.trim() || null
    });
  }

  /** Development API only — link with link token from Open bot in Telegram. */
  completeTelegramLinkManual(linkToken: string, telegramChatId: number): Promise<{ linked: boolean }> {
    return apiService.post('/api/Auth/two-factor/telegram/complete-link', {
      linkToken,
      telegramChatId
    });
  }

  /** Development API only — verify bot token (getMe). */
  getTelegramBotStatus(): Promise<{
    configured: boolean;
    ok: boolean;
    username?: string;
    configuredUsername?: string;
    error?: string;
  }> {
    return apiService.get('/api/Auth/two-factor/telegram/bot-status', { cache: false });
  }

  getBaleLink(): Promise<{ linkToken: string; deepLink: string; botUsername: string }> {
    return apiService.get('/api/Auth/two-factor/bale/link', { cache: false });
  }

  getBaleLinkStatus(linkToken: string): Promise<{ linked: boolean; phoneNumber?: string }> {
    return apiService.get(`/api/Auth/two-factor/bale/link-status?linkToken=${encodeURIComponent(linkToken)}`, {
      cache: false
    });
  }

  /** Development API only — link by Bale user id (from getUpdates), no link token required. */
  linkBaleUserId(baleUserId: string, phoneNumber?: string): Promise<{ linked: boolean; phoneNumber?: string }> {
    return apiService.post('/api/Auth/two-factor/bale/link-user-id', {
      baleUserId: baleUserId.trim(),
      phoneNumber: phoneNumber?.trim() || null
    });
  }

  /** Development API only — link with link token from Open bot link. */
  completeBaleLinkManual(linkToken: string, baleUserId: string): Promise<{ linked: boolean }> {
    return apiService.post('/api/Auth/two-factor/bale/complete-link', {
      linkToken,
      baleUserId: baleUserId.trim()
    });
  }

  /** Development API only — verify bot token (getMe). */
  getBaleBotStatus(): Promise<{
    configured: boolean;
    ok: boolean;
    username?: string;
    configuredUsername?: string;
    error?: string;
  }> {
    return apiService.get('/api/Auth/two-factor/bale/bot-status', { cache: false });
  }
}

export const twoFactorService = new TwoFactorService();
